import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Send, X, Heart, MessageCircle, Share2, ChevronLeft } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import type { WPPost } from '../../services/wordpressApi';
import * as wpApi from '../../services/wordpressApi';
import { useAppStore } from '../../stores/wpAppStore';
import Avatar from '../layout/Avatar';
import MatchResultCard from './MatchResultCard';

interface CommentSheetProps {
  open: boolean;
  post: WPPost | null;
  onClose: () => void;
}

function toMilliseconds(timestamp: number): number {
  return timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
}

function timeAgo(timestamp: number): string {
  try {
    return formatDistanceToNow(new Date(toMilliseconds(timestamp)), { addSuffix: true, locale: vi });
  } catch {
    return 'vừa xong';
  }
}

function renderContent(text: string): React.ReactNode[] {
  const parts = text.split(/(#[\p{L}0-9_-]+|@[\p{L}0-9_-]+)/gu);
  return parts.map((part, index) => {
    if (part.startsWith('#')) {
      return (
        <span key={index} className="font-semibold text-blue-500">{part}</span>
      );
    }
    if (part.startsWith('@')) {
      return (
        <span key={index} className="font-semibold text-orange-500">{part}</span>
      );
    }
    return <span key={index}>{part}</span>;
  });
}

const SUBCATEGORY_LABELS: Record<string, string> = {
  'Snooker': '🎱 Snooker',
  'Pool9': '🎯 Pool 9',
  '3C': '🎯 3 Cushion',
  'Libre': '🎯 Libre',
  'default': '📝 Bài viết',
};

const styles = {
  backdrop: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    zIndex: 60,
  },
  wrapper: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 61,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0',
  },
  sheet: {
    width: 'min(600px, 92vw)',
    maxWidth: '600px',
    height: '80vh',
    background: '#18181b',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    boxShadow: '0 24px 80px rgba(0, 0, 0, 0.6)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px 8px 6px',
    borderBottom: '1px solid #27272a',
    flexShrink: 0,
    background: '#18181b',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#a1a1aa',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '9999px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s',
  },
  headerTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#fafafa',
    flex: 1,
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto' as const,
    WebkitOverflowScrolling: 'touch' as const,
  },
  postSection: {
    padding: '14px 16px',
    borderBottom: '1px solid #27272a',
    flexShrink: 0,
    maxHeight: '42vh',
    overflowY: 'auto' as const,
  },
  authorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
  },
  authorMeta: {
    minWidth: 0,
    flex: 1,
  },
  authorName: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#fafafa',
  },
  authorTime: {
    fontSize: '12px',
    color: '#71717a',
    display: 'block' as const,
  },
  sublabel: {
    fontSize: '12px',
    color: '#a1a1aa',
    marginTop: '2px',
    display: 'block' as const,
  },
  content: {
    fontSize: '15px',
    lineHeight: 1.6,
    color: '#fafafa',
    wordBreak: 'break-word' as const,
    marginBottom: '12px',
  },
  hashtags: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
    marginBottom: '12px',
  },
  hashtag: {
    padding: '6px 14px',
    background: 'rgba(59, 130, 246, 0.15)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '9999px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#60a5fa',
    transition: 'all 0.2s',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    paddingTop: '8px',
    borderTop: '1px solid #27272a',
  },
  actionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    background: 'none',
    border: 'none',
    color: '#71717a',
    fontSize: '14px',
    cursor: 'pointer',
    borderRadius: '8px',
    transition: 'all 0.2s',
    fontFamily: 'inherit' as const,
  },
  actionBtnLiked: {
    color: '#ef4444',
  },
  commentsHeader: {
    padding: '12px 16px 4px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#a1a1aa',
  },
  comment: {
    display: 'flex',
    gap: '12px',
    padding: '10px 16px',
    alignItems: 'flex-start',
  },
  commentName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#fafafa',
  },
  commentTime: {
    fontSize: '12px',
    color: '#71717a',
  },
  commentText: {
    fontSize: '14px',
    color: '#d4d4d8',
    lineHeight: 1.5,
    margin: 0,
    wordBreak: 'break-word' as const,
  },
  empty: {
    padding: '40px 16px',
    textAlign: 'center' as const,
    color: '#71717a',
    fontSize: '14px',
  },
  loading: {
    padding: '40px 16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  spinner: {
    width: '28px',
    height: '28px',
    border: '2px solid #27272a',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    paddingBottom: 'calc(10px + env(safe-area-inset-bottom))',
    borderTop: '1px solid #27272a',
    background: '#18181b',
    flexShrink: 0,
  },
  input: {
    flex: 1,
    padding: '9px 16px',
    background: '#27272a',
    border: '1px solid #27272a',
    borderRadius: '9999px',
    color: '#fafafa',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  submitBtn: {
    width: '38px',
    height: '38px',
    flexShrink: 0,
    background: '#3b82f6',
    border: 'none',
    borderRadius: '50%',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
};

export default function CommentSheet({ open, post, onClose }: CommentSheetProps) {
  const posts = useAppStore((s) => s.posts);
  const currentUser = useAppStore((s) => s.currentUser);
  const likePost = useAppStore((s) => s.likePost);
  const unlikePost = useAppStore((s) => s.unlikePost);
  const refreshPost = useAppStore((s) => s.refreshPost);

  // Luôn lấy bản mới nhất của bài viết từ store → like/comment cập nhật live
  const livePost = useMemo(
    () => (post ? posts.find((p) => p.id === post.id) ?? post : null),
    [posts, post]
  );

  const [comments, setComments] = useState<wpApi.WPComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (open && post) {
      setComments([]);
      setImageError(false);
      loadComments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, post?.id]);

  const loadComments = async () => {
    if (!post) return;
    setLoading(true);
    try {
      const data = await wpApi.getComments(post.id);
      setComments(data);
    } catch {
      toast.error('Không thể tải bình luận');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!post || !newComment.trim()) return;

    if (!currentUser) {
      toast.error('Vui lòng đăng nhập để bình luận');
      return;
    }

    setSubmitting(true);
    try {
      const comment = await wpApi.addComment(post.id, newComment.trim());
      setComments((prev) => [comment, ...prev]);
      setNewComment('');
      // Reload bài viết để cập nhật commentCount trong feed list
      refreshPost(post.id);
    } catch (err: any) {
      toast.error(err?.message || 'Không thể gửi bình luận');
    } finally {
      setSubmitting(false);
    }
  };

  const isLiked =
    !!(currentUser && livePost && livePost.likes.includes(currentUser.uid));

  const handleLike = () => {
    if (!currentUser || !livePost) return;
    if (isLiked) unlikePost(livePost.id);
    else likePost(livePost.id);
  };

  const handleShare = async () => {
    if (!livePost) return;
    const text = livePost.content;
    if (typeof navigator !== 'undefined' && navigator.share) {
      await navigator.share({ title: livePost.ownerName, text });
      try {
        await wpApi.sharePost(livePost.id);
      } catch {
        // Keep quiet
      }
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      toast.success('Đã sao chép nội dung để chia sẻ');
    }
  };

  const openLogin = () => {
    toast.error('Vui lòng đăng nhập để bình luận');
    window.dispatchEvent(new CustomEvent('vncues:open-login-modal'));
  };

  return (
    <AnimatePresence>
      {open && livePost && (
        <>
          <motion.div
            style={styles.backdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.div
            style={styles.wrapper}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              style={styles.sheet}
              className="rounded-2xl border border-zinc-800 shadow-2xl"
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            >
              {/* Header */}
              <div style={styles.header}>
                <button
                  style={styles.closeBtn}
                  onClick={onClose}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#27272a')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                  title="Đóng"
                >
                  <ChevronLeft size={22} className="sm:hidden" />
                  <X size={20} className="hidden sm:block" />
                </button>
                <span style={styles.headerTitle}>Bài viết</span>
              </div>

              {/* Post section — cố định ở trên */}
              <div style={styles.postSection}>
                <div style={styles.authorRow}>
                    <Avatar name={livePost.ownerName} photoURL={livePost.ownerPhotoURL} size={44} />
                    <div style={styles.authorMeta}>
                      <div style={styles.authorName}>{livePost.ownerName}</div>
                      <time
                        style={styles.authorTime}
                        dateTime={
                          Number.isFinite(Number(livePost.createdAt))
                            ? new Date(toMilliseconds(Number(livePost.createdAt))).toISOString()
                            : undefined
                        }
                      >
                        {timeAgo(Number(livePost.createdAt))}
                      </time>
                      <span style={styles.sublabel}>
                        {livePost.type === 'match'
                          ? '🏆 Kết quả đấu'
                          : SUBCATEGORY_LABELS[livePost.subCategory] || SUBCATEGORY_LABELS['default']}
                      </span>
                    </div>
                  </div>

                  {livePost.content && (
                    <p style={styles.content}>
                      {renderContent(livePost.content)}
                    </p>
                  )}

                  {livePost.imageURL && !imageError && (
                    <div className="mb-3 overflow-hidden rounded-xl">
                      <img
                        src={livePost.imageURL}
                        alt=""
                        loading="lazy"
                        onError={() => setImageError(true)}
                        className="block w-full rounded-xl"
                      />
                    </div>
                  )}

                  {livePost.matchData && (
                    <div className="mb-3">
                      <MatchResultCard match={livePost.matchData} />
                    </div>
                  )}

                  {livePost.hashtags.length > 0 && (
                    <div style={styles.hashtags}>
                      {livePost.hashtags.slice(0, 6).map((tag) => (
                        <span key={tag} style={styles.hashtag}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Action bar */}
                  <div style={styles.actions}>
                    <motion.button
                      style={{
                        ...styles.actionBtn,
                        ...(isLiked ? styles.actionBtnLiked : {}),
                        cursor: currentUser ? 'pointer' : 'not-allowed',
                      }}
                      onClick={handleLike}
                      whileTap={{ scale: 0.9 }}
                    >
                      <motion.span
                        animate={isLiked ? { scale: [1, 1.3, 1] } : {}}
                        transition={{ duration: 0.3 }}
                      >
                        <Heart size={18} fill={isLiked ? 'currentColor' : 'none'} />
                      </motion.span>
                      <span>{livePost.likeCount || ''}</span>
                    </motion.button>

                    <button style={styles.actionBtn} onClick={() => {}}>
                      <MessageCircle size={18} />
                      <span>{livePost.commentCount || ''}</span>
                    </button>

                    <button style={{ ...styles.actionBtn, marginLeft: 'auto' }} onClick={handleShare}>
                      <Share2 size={18} />
                    </button>
                  </div>
                </div>

                {/* Comments — cuộn trong phần còn lại */}
                <div style={styles.body}>
                  <div style={styles.commentsHeader}>
                    Bình luận ({comments.length})
                  </div>

                {loading ? (
                  <div style={styles.loading}>
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    <div style={styles.spinner} />
                  </div>
                ) : comments.length === 0 ? (
                  <div style={styles.empty}>Chưa có bình luận nào. Hãy là người đầu tiên!</div>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} style={styles.comment}>
                      <Avatar name={comment.authorName} photoURL={comment.authorPhoto} size={32} />
                      <div className="min-w-0">
                        <div className="mb-0.5 flex items-center gap-2">
                          <span style={styles.commentName}>{comment.authorName}</span>
                          <span style={styles.commentTime}>{timeAgo(comment.createdAt)}</span>
                        </div>
                        <p style={styles.commentText}>{comment.content}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Input */}
              {currentUser ? (
                <form style={styles.footer} onSubmit={handleSubmit}>
                  <Avatar name={currentUser.displayName} photoURL={currentUser.photoURL} size={30} />
                  <input
                    type="text"
                    style={styles.input}
                    placeholder="Viết bình luận..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    disabled={submitting}
                    onFocus={(e) => (e.target.style.borderColor = '#3b82f6')}
                    onBlur={(e) => (e.target.style.borderColor = '#27272a')}
                  />
                  <motion.button
                    style={{
                      ...styles.submitBtn,
                      opacity: !newComment.trim() || submitting ? 0.5 : 1,
                      cursor: !newComment.trim() || submitting ? 'not-allowed' : 'pointer',
                    }}
                    type="submit"
                    disabled={!newComment.trim() || submitting}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Send size={17} />
                  </motion.button>
                </form>
              ) : (
                <button
                  style={styles.footer}
                  onClick={openLogin}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#27272a')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '#18181b')}
                >
                  <span style={{ flex: 1, padding: '9px 16px', background: '#27272a', borderRadius: '9999px', color: '#71717a', fontSize: '14px', textAlign: 'left' as const }}>
                    Đăng nhập để bình luận
                  </span>
                </button>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}